"use client"

import { useHubPreview, type HubPreviewSound } from "@/hooks/useHubPreview"
import HubPreviewButton from "./hub-preview-button"

interface CollectionHeroProps {
  title: string
  accentToken: string
  previewSounds: HubPreviewSound[]
}

export default function CollectionHero({
  title,
  accentToken,
  previewSounds,
}: CollectionHeroProps) {
  const { isPlaying, activeIndex, isLoading, toggle } = useHubPreview({
    previewSounds,
    coordinatorId: `collection-hero-${title}`,
  })

  const accentRgb = `rgb(var(${accentToken}))`
  const accentSoft = `rgba(var(${accentToken}), 0.16)`
  const accentEdge = `rgba(var(${accentToken}), 0.42)`

  return (
    // No backdrop and no negative margins (TES-1836). This block previously
    // painted its own blurred cover art and pulled itself up over the header
    // with `-mt-4/-mt-10`, which sliced the H1 in half and covered the
    // collection description entirely — measured on prod, the `<p>` rendered at
    // y=264 underneath a hero starting at y=252, so no collection page has ever
    // shown its own description. The `max-w-7xl` wrapper also made the
    // "full-bleed" band an inset 1280px slab at wider viewports, reading as a
    // broken image rather than a design element. The cover now goes through
    // LibraryPageHeader's `backgroundImage`, which is genuinely full-bleed and
    // already carries the canvas gradient and left scrim.
    <div className="mt-8">
      <HubPreviewButton
        previewSounds={previewSounds}
        accentColor={accentRgb}
        isPlaying={isPlaying}
        activeIndex={activeIndex}
        isLoading={isLoading}
        onToggle={toggle}
      />

      {isPlaying && previewSounds.length > 0 && (
        <ol
          aria-live="polite"
          className="mt-6 flex flex-wrap gap-2 text-sm text-[var(--tls-fg-muted)]"
        >
          {previewSounds.map((s, i) => {
            const active = i === activeIndex
            return (
              <li
                key={s.id}
                className="inline-flex items-center gap-2 rounded-full border px-3 py-1.5 transition-colors"
                style={{
                  borderColor: active ? accentEdge : "var(--tls-border-subtle)",
                  background: active ? accentSoft : "var(--tls-surface)",
                  color: active ? "var(--tls-fg-strong)" : undefined,
                }}
              >
                <span
                  aria-hidden
                  className="inline-block size-1.5 rounded-full"
                  style={{ background: active ? accentRgb : "var(--tls-fg-subtle)" }}
                />
                {s.title}
              </li>
            )
          })}
        </ol>
      )}
    </div>
  )
}
